import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Lock, Sparkles, Brain, BookOpen, Languages, Zap } from "lucide-react";
import { auth } from "@/lib/firebase";
import { PRICING } from "@/lib/pricing";

const FEATURE_DESCRIPTIONS: Record<string, { title: string; description: string; icon: React.ReactNode }> = {
  grammar: {
    title: "Unlimited Grammar Analysis",
    description: "Get deep grammar breakdowns for every word — conjugations, declensions, and more.",
    icon: <Brain className="h-5 w-5" />,
  },
  vocabulary: {
    title: "AI Vocabulary Generation",
    description: "Generate study sets on any topic with AI-powered word lists.",
    icon: <BookOpen className="h-5 w-5" />,
  },
  categorize: {
    title: "Smart Categorization",
    description: "Automatically categorize words as you add them to your word bank.",
    icon: <Sparkles className="h-5 w-5" />,
  },
  llmTranslate: {
    title: "AI Translation",
    description: "Get more natural, context-aware translations powered by AI.",
    icon: <Languages className="h-5 w-5" />,
  },
};

const PRO_FEATURES = [
  "Unlimited grammar analysis",
  "AI vocabulary generation",
  "Smart auto-categorization",
  "AI-powered translations",
  "Grammar table & sentence audio pronunciation",
];

export function UpgradePrompt() {
  const { showUpgradePrompt, closeUpgradePrompt, upgradeFeature } =
    useSubscription();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const featureInfo = upgradeFeature
    ? FEATURE_DESCRIPTIONS[upgradeFeature]
    : null;

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const response = await fetch("/api/stripe-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || "Failed to start checkout");
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Failed to start checkout:", error);
      setCheckoutError(
        error instanceof Error ? error.message : "Something went wrong. Please try again."
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <Dialog open={showUpgradePrompt} onOpenChange={closeUpgradePrompt}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-500" />
            Upgrade to Pro
          </DialogTitle>
          <DialogDescription>
            {featureInfo
              ? featureInfo.description
              : "Unlock all AI-powered features to supercharge your Hebrew learning."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {featureInfo && (
            <div className="flex items-center gap-3 rounded-lg bg-amber-50 p-3">
              <div className="text-amber-600">{featureInfo.icon}</div>
              <div>
                <p className="font-medium text-amber-900">
                  {featureInfo.title}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Everything in Pro:
            </p>
            {PRO_FEATURES.map((feature) => (
              <div key={feature} className="flex items-center gap-2 text-sm">
                <Zap className="h-3.5 w-3.5 text-amber-500" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            {checkoutError && (
              <p className="text-center text-sm text-red-600">{checkoutError}</p>
            )}
            <Button
              onClick={handleUpgrade}
              disabled={checkoutLoading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              {checkoutLoading ? "Loading..." : (
                <>
                  Upgrade to Pro —{" "}
                  {PRICING.isPromoActive && (
                    <span className="line-through opacity-70 mr-1">
                      ${PRICING.originalPrice}
                    </span>
                  )}
                  ${PRICING.currentPrice}/month
                </>
              )}
            </Button>
            {PRICING.isPromoActive && (
              <p className="text-center text-xs font-medium text-amber-600">
                {PRICING.promoName} — Limited time offer
              </p>
            )}
          </div>

          <p className="text-center text-xs text-gray-500">
            Cancel anytime. You keep access until the end of your billing period.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
